import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { TestSubmissionTrashItem } from "@/api/tests.api";
import type { useGetPaid } from "../useGetPaid";

type Props = {
  t: ReturnType<typeof useGetPaid>["t"];
  items: TestSubmissionTrashItem[];
  isLoading: boolean;
  hasError: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onRestore: (id: string) => Promise<void>;
};

export function TestSubmissionRecycleBinDialog({
  t,
  items,
  isLoading,
  hasError,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onRestore,
}: Props) {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const restore = async (id: string) => {
    setBusyId(id);
    try {
      await onRestore(id);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        {t.getPaid.testRecycleBin}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.getPaid.testRecycleBin}</DialogTitle>
            <DialogDescription>{t.getPaid.testRecycleBinDescription}</DialogDescription>
          </DialogHeader>
          {isLoading ? <p>{t.getPaid.loading}</p> : null}
          {hasError ? <p role="alert">{t.getPaid.requestsLoadError}</p> : null}
          {!isLoading && !hasError && items.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t.getPaid.testTrashEmpty}</p>
          ) : null}
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={item.id} className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 text-sm">
                  <p className="font-medium">{item.userId} · {item.language} · {item.status}</p>
                  <p className="text-muted-foreground">{item.deletionReason}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(item.restoreUntil).toLocaleString()}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busyId !== null || new Date(item.restoreUntil).getTime() < Date.now()}
                  onClick={() => void restore(item.id)}
                >
                  {busyId === item.id ? t.getPaid.loading : t.getPaid.testTrashRestore}
                </Button>
              </li>
            ))}
          </ul>
          {hasMore ? (
            <Button type="button" variant="outline" disabled={isLoadingMore} onClick={onLoadMore}>
              {isLoadingMore ? t.getPaid.loading : t.getPaid.testTrashLoadMore}
            </Button>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
